import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Camera, CameraSource, CameraResultType } from '@capacitor/camera';
import { Share } from '@capacitor/share';

export const STARTUS_AUTH_CALLBACK_URL = 'startus://auth/callback';

const AUTH_FLOW_STORAGE_KEY = 'startus:auth:pending-flow';
const AUTH_CALLBACK_STORAGE_KEY = 'startus:auth:last-callback-id';
const AUTH_FLOW_TIMEOUT_MS = 3 * 60 * 1000;

function getStorage() {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.localStorage;
}

function readJson(key) {
  try {
    const raw = getStorage()?.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeJson(key, value) {
  try {
    getStorage()?.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore storage write errors to avoid blocking auth.
  }
}

function removeValue(key) {
  try {
    getStorage()?.removeItem(key);
  } catch {
    // Ignore storage cleanup errors.
  }
}

function randomNonce() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `auth-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function buildCallbackId(payload) {
  return [
    payload?.provider || 'unknown',
    payload?.stateNonce || 'none',
    payload?.token ? 'token' : `error:${payload?.error || 'none'}`,
  ].join(':');
}

export function isNativeApp() {
  return Capacitor.isNativePlatform();
}

export function addAppUrlOpenListener(listener) {
  if (!isNativeApp()) {
    return null;
  }

  return CapacitorApp.addListener('appUrlOpen', listener);
}

export function addAppResumeListener(listener) {
  if (!isNativeApp()) {
    return null;
  }

  return CapacitorApp.addListener('resume', listener);
}

export async function openAuthSession(url) {
  if (!isNativeApp()) {
    window.location.href = url;
    return;
  }

  await Browser.open({
    url,
    presentationStyle: 'fullscreen',
  });
}

export async function closeAuthSession() {
  if (!isNativeApp()) {
    return;
  }

  try {
    await Browser.close();
  } catch {
    // Browser.close is best-effort and may no-op on Android.
  }
}

export function beginAuthFlow(provider, returnUrl, returnTo = '') {
  const flow = {
    provider,
    returnUrl,
    returnTo,
    stateNonce: randomNonce(),
    status: 'pending',
    startedAt: Date.now(),
    completedAt: null,
    lastError: '',
  };

  writeJson(AUTH_FLOW_STORAGE_KEY, flow);
  return flow;
}

export function readPendingAuthFlow() {
  const flow = readJson(AUTH_FLOW_STORAGE_KEY);
  if (!flow) {
    return null;
  }

  if (!flow.startedAt || Date.now() - flow.startedAt > AUTH_FLOW_TIMEOUT_MS) {
    removeValue(AUTH_FLOW_STORAGE_KEY);
    return null;
  }

  return flow;
}

export function markAuthFlowResult(status, lastError = '') {
  const flow = readPendingAuthFlow();
  if (!flow) {
    return null;
  }

  const nextFlow = {
    ...flow,
    status,
    lastError,
    completedAt: Date.now(),
  };

  writeJson(AUTH_FLOW_STORAGE_KEY, nextFlow);
  return nextFlow;
}

export function clearAuthFlow() {
  removeValue(AUTH_FLOW_STORAGE_KEY);
}

export function isAuthFlowExpired(flow) {
  return !flow?.startedAt || Date.now() - flow.startedAt > AUTH_FLOW_TIMEOUT_MS;
}

export function hasProcessedAuthCallback(callbackId) {
  if (!callbackId) {
    return false;
  }

  const lastCallback = readJson(AUTH_CALLBACK_STORAGE_KEY);
  if (!lastCallback?.callbackId) {
    return false;
  }

  if (Date.now() - (lastCallback.handledAt || 0) > AUTH_FLOW_TIMEOUT_MS) {
    removeValue(AUTH_CALLBACK_STORAGE_KEY);
    return false;
  }

  return lastCallback.callbackId === callbackId;
}

export function markAuthCallbackProcessed(payload) {
  const callbackId = buildCallbackId(payload);
  writeJson(AUTH_CALLBACK_STORAGE_KEY, {
    callbackId,
    handledAt: Date.now(),
  });
  return callbackId;
}

export function parseAuthCallbackUrl(urlString) {
  if (!urlString) {
    return {
      shouldHandle: false,
      parseError: 'empty_url',
    };
  }

  let url;
  try {
    url = new URL(urlString);
  } catch {
    return {
      shouldHandle: true,
      parseError: 'invalid_callback_url',
    };
  }

  const isNativeCallback = url.protocol === 'startus:' && url.hostname === 'auth' && url.pathname === '/callback';
  const isWebCallback = /^https?:$/i.test(url.protocol) && url.pathname === '/login';
  const hash = url.hash.startsWith('#') ? url.hash.slice(1) : '';
  const hashParams = new URLSearchParams(hash);
  const params = new URLSearchParams(url.search);
  const hasAuthPayload = Boolean(
    hashParams.get('token') ||
    params.get('token') ||
    hashParams.get('error') ||
    params.get('error') ||
    hashParams.get('state_nonce') ||
    params.get('state_nonce') ||
    hashParams.get('provider') ||
    params.get('provider')
  );

  if (!isNativeCallback && !isWebCallback) {
    return {
      shouldHandle: false,
      parseError: '',
    };
  }

  if (isWebCallback && !hasAuthPayload) {
    return {
      shouldHandle: false,
      parseError: '',
    };
  }

  const payload = {
    shouldHandle: true,
    isNativeCallback,
    parseError: '',
    token: hashParams.get('token') || params.get('token') || '',
    error: hashParams.get('error') || params.get('error') || '',
    errorDescription:
      hashParams.get('error_description') ||
      params.get('error_description') ||
      '',
    provider: hashParams.get('provider') || params.get('provider') || '',
    stateNonce: hashParams.get('state_nonce') || params.get('state_nonce') || '',
    returnTo: hashParams.get('returnTo') || params.get('returnTo') || '',
  };

  payload.callbackId = buildCallbackId(payload);
  return payload;
}

function inferFileExtension(format = 'jpeg') {
  const normalized = String(format).toLowerCase();
  if (normalized === 'png') {
    return 'png';
  }

  if (normalized === 'gif') {
    return 'gif';
  }

  if (normalized === 'webp') {
    return 'webp';
  }

  return 'jpeg';
}

export function dataUrlToFile(dataUrl, filenameBase = 'image') {
  const [header, body] = String(dataUrl || '').split(',');
  if (!header || !body) {
    throw new Error('Invalid image payload');
  }

  const mimeMatch = header.match(/data:(.*?);base64/i);
  const mimeType = mimeMatch?.[1] || 'image/jpeg';
  const extension = inferFileExtension(mimeType.split('/')[1]);
  const binary = window.atob(body);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return new File([bytes], `${filenameBase}.${extension}`, { type: mimeType });
}

export async function pickNativeImage(source = 'photos', filenameBase = 'image') {
  if (!isNativeApp()) {
    return null;
  }

  const cameraSource = source === 'camera' ? CameraSource.Camera : CameraSource.Photos;
  let photo;
  try {
    photo = await Camera.getPhoto({
      source: cameraSource,
      resultType: CameraResultType.DataUrl,
      quality: 88,
      correctOrientation: true,
    });
  } catch (error) {
    const message = String(error?.message || '').toLowerCase();
    const wrappedError = new Error(error?.message || 'Failed to access image source');

    if (message.includes('cancel')) {
      wrappedError.code = 'cancelled';
      throw wrappedError;
    }

    if (message.includes('permission') || message.includes('denied')) {
      wrappedError.code = 'permission_denied';
      throw wrappedError;
    }

    wrappedError.code = 'native_pick_failed';
    throw wrappedError;
  }

  if (!photo?.dataUrl) {
    const error = new Error('Image selection was cancelled');
    error.code = 'cancelled';
    throw error;
  }

  return {
    format: photo.format || 'jpeg',
    dataUrl: photo.dataUrl,
    file: dataUrlToFile(photo.dataUrl, filenameBase),
  };
}

export async function shareImageFile({ title, text, file }) {
  if (isNativeApp()) {
    await Share.share({
      title,
      text,
      files: file ? [file] : undefined,
    });
    return true;
  }

  if (navigator.share && file) {
    if (!navigator.canShare || navigator.canShare({ files: [file] })) {
      await navigator.share({
        title,
        text,
        files: [file],
      });
      return true;
    }
  }

  return false;
}
